import DataProviderService from 'services/DataProviderService';
import localForage from 'localforage';
import pako from 'pako';

const markersDefaultFilters = {
  userId: 'intelstream',
  dashboardId: 'ni-ran-advanced-map-dashboard',
  zoomLevel: 11,
  viewPort: [
    { lat: 25.203917565484744, lng: 51.31606628417971 },
    { lat: 25.356034751361875, lng: 51.72393371582034 },
  ],
  exclusionViewPort: [
    { lat: 0, lng: 0 },
    { lat: 0, lng: 0 },
  ],
  layerIds: ['SECTOR_LOCATION_4G'],
};

const storeData = (key, value) => {
  const compressionWorker = new Worker(
    new URL('./compressionWorker.js', import.meta.url),
    { type: 'module' }
  );

  compressionWorker.postMessage({ action: 'compress', key: key, value: value });

  compressionWorker.onmessage = async (e) => {
    const { success, compressedData, error } = e.data;

    if (success) {
      await localForage.setItem(key, compressedData);
    } else {
      console.error('Compression worker failed:', error);
    }
    try {
      compressionWorker.terminate();
    } catch (e) {
      // dont care about this catch
    }
  };
};

const troubleTicketsDefaultFilters = {
  ...markersDefaultFilters,
  zoomLevel: 3,
  podId: 'ni-ran-advanced-map-dashboard-trouble-tickets',
};

export const checkIfCacheExists = async (cacheKey) => {
  const cachedData = await localForage.getItem(cacheKey);
  if (cachedData) {
    const pakipaki = pako.inflate(cachedData, { to: 'string' });
    const js = JSON.parse(pakipaki);
    return js;
  } else {
    return null;
  }
};

export const removeItemIfExists = async (cacheKey) => {
  try {
    await localForage.removeItem(cacheKey);
  } catch (error) {
    console.error(error);
  }
};

export const geoJsonLayers = async (objectToCache) => {
  const cacheKey = objectToCache.layer;

  const compressionWorker = new Worker(
    new URL('./compressionWorker.js', import.meta.url),
    { type: 'module' }
  );

  compressionWorker.postMessage({
    action: 'compress',
    key: cacheKey,
    value: objectToCache,
  });

  compressionWorker.onmessage = async (e) => {
    const { success, compressedData, error } = e.data;

    if (success) {
      await localForage.setItem(cacheKey, compressedData);
    } else {
      console.error('Compression worker failed:', error);
    }
    try {
      compressionWorker.terminate();
    } catch (e) {
      // dont care about this catch
    }
  };
};

export const fetchMarkers = async (filtersObject) => {
  const cacheKey = JSON.stringify(filtersObject); // Create a unique key for the filters

  const cachedData = await checkIfCacheExists(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  const dpService = new DataProviderService();
  // dont use server side cache when it is KPI request
  // when doing the region markeres cell request we use cache
  let disableCache = filtersObject.datafilters.some(
    (filter) => filter.filterId == 'global_kpi_filter'
  );
  const markersApi = await dpService.getAdvancedMapData({
    ...markersDefaultFilters,
    disableCache: disableCache,
    ...filtersObject,
  });

  if (!markersApi || !markersApi.data) {
    throw new Error('Invalid data structure returned from API.');
  }

  const data = markersApi.data;

  storeData(cacheKey, data);

  return data;
};

export const fetchElementDetailsLayerData = async (
  networkElementMarkerInfo,
  granularityDates,
  elementDetailLayerDefinition,
  filtersState
) => {
  // networkElementMarkerInfo can be Cell or Site
  let elementLevelValue = networkElementMarkerInfo.type ?? 'cell';
  let elementFilter = {
    filterId: 'element_filter',
    values: [
      {
        id: 'technology',
        name: networkElementMarkerInfo['TECHNOLOGY'],
      },
      {
        id: 'site_natural_key',
        name: networkElementMarkerInfo['SITE_NATURAL_KEY'],
      },
    ],
  };
  if (elementLevelValue !== 'site')
    elementFilter.values.push({
      id: 'cell_natural_key',
      name: networkElementMarkerInfo.id,
    });
  let elementLevel = {
    filterId: 'element_level',
    values: [
      {
        id: elementLevelValue,
        name: elementLevelValue,
      },
    ],
  };
  // check if this layer requires any element property mapping
  // and also if the requested property exists for the eleent
  let elementAttrFilters =
    elementDetailLayerDefinition.LayerProperties?.ElementAttributesFilterMapping?.filter(
      (filterMapping) => 
        filterMapping.PropertyLevel == elementLevelValue &&
        networkElementMarkerInfo[
          filterMapping.ClickedElementPropertyName
        ]
    ).map((filterMapping) => {
      return {
        filterId: 'element_attr_' + filterMapping.ClickedElementPropertyName,
        values: [
          {
            id: networkElementMarkerInfo[
              filterMapping.ClickedElementPropertyName
            ],
            name: networkElementMarkerInfo[
              filterMapping.ClickedElementPropertyName
            ],
          },
        ],
      };
    }) || [];

  // add all the filters from the filterState
  let clonedFilters = filtersState
    // for now only include Neighbour KPI ones
    .filter((filter) => filter.category === 'mapNeighborKpiFilter')
    .map((filter) => {
      return { ...filter, values: filter.selectedValues };
    });

  let requestParams = {
    dashboardId: 'ni-ran-advanced-map-dashboard',
    podId: 'ni-ran-advanced-map-dashboard-element-details-layer',
    layerIds: [elementDetailLayerDefinition.LayerId],
    dataFilters: [
      elementFilter,
      elementLevel,
      ...elementAttrFilters,
      granularityDates,
      ...clonedFilters,
    ],
  };
  const dpService = new DataProviderService();
  const layerData = await dpService.getMapGeoJsonData(requestParams);
  return layerData.data;
};

export const fetchCellTroubleTickets = async (
  cellId,
  siteId,
  region,
  technology,
  granularityDates
) => {
  let additionalFilters = {
    dataFilters: [
      {
        filterId: 'synch_cell_natural_key_filter',
        values: [
          {
            id: cellId,
            name: cellId,
          },
        ],
      },{
        filterId: 'synch_site_natural_key_filter',
        values: [
          {
            id: siteId,
            name: siteId,
          },
        ],
      },
      {
        filterId: 'global_region_filter',
        values: [
          {
            id: region,
            name: region,
          },
        ],
      },
      {
        filterId: 'global_technology_filter',
        values: [
          {
            id: technology,
            name: technology,
          },
        ],
      },
      granularityDates,
    ],
  };
  const dpService = new DataProviderService();
  const troubleTicketApi = await dpService.getData({
    ...troubleTicketsDefaultFilters,
    ...additionalFilters,
  });
  if (!troubleTicketApi || !troubleTicketApi.data) {
    throw new Error('Invalid data structure returned from API.');
  }
  return troubleTicketApi;
};
